/**
 * Authentication Service
 * Handles AWS Cognito authentication operations
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import { cognitoConfig, adminEmail } from '../config/cognito';

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.userPoolWebClientId
});

class AuthService {
  /**
   * Get current authenticated user
   */
  getCurrentUser() {
    console.log('Getting current user from pool...');
    console.log('LocalStorage keys:', Object.keys(localStorage));
    const user = userPool.getCurrentUser();
    console.log('Current user:', user ? user.getUsername() : 'null');
    return user;
  }

  /**
   * Get user session
   */
  getUserSession() {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(session);
      });
    });
  }

  /**
   * Get user attributes
   */
  getUserAttributes() {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const attrs = {};
        attributes.forEach(attr => {
          attrs[attr.Name] = attr.Value;
        });
        resolve(attrs);
      });
    });
  }

  /**
   * Check if user is admin
   */
  async isAdmin() {
    try {
      const attributes = await this.getUserAttributes();
      return attributes.email === adminEmail;
    } catch (error) {
      return false;
    }
  }

  /**
   * Sign in user
   */
  signIn(username, password) {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          console.log('Authentication successful, session should be stored');
          console.log('Session tokens received:', {
            accessToken: result.getAccessToken().getJwtToken().substring(0, 20) + '...',
            hasRefreshToken: !!result.getRefreshToken()
          });
          resolve({
            accessToken: result.getAccessToken().getJwtToken(),
            idToken: result.getIdToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken()
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // User needs to set a new password
          resolve({
            challengeName: 'NEW_PASSWORD_REQUIRED',
            userAttributes,
            requiredAttributes,
            cognitoUser
          });
        }
      });
    });
  }

  /**
   * Complete new password challenge
   */
  completeNewPassword(cognitoUser, newPassword, userAttributes) {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
        onSuccess: (result) => {
          resolve({
            accessToken: result.getAccessToken().getJwtToken(),
            idToken: result.getIdToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken()
          });
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  /**
   * Request account (for admin approval)
   * This sends a request to the backend to create a user
   */
  async requestAccount(email, name) {
    // Abort the request after 15s so the form never hangs on "Submitting..."
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch('/api/auth/request-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, name }),
        signal: controller.signal
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to request account');
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again or contact the administrator.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Verify email with code
   */
  verifyEmail(username, code) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  /**
   * Resend verification code
   */
  resendVerificationCode(username) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  /**
   * Forgot password
   */
  forgotPassword(username) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          resolve(data);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  /**
   * Confirm password reset
   */
  confirmPassword(username, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve('Password changed successfully');
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  /**
   * Sign out user
   */
  signOut() {
    const cognitoUser = this.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
  }

  /**
   * Change password
   */
  changePassword(oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
        });
      });
    });
  }
}

export default new AuthService();
